import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { forceVisible } from 'react-lazyload';
import { useTranslation } from 'react-i18next';
// Types
import { RootState } from 'app/store';
// Store
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Actions
import PageActions from 'app/store/page/page.actions';
// Selectors
import { selectDocumentEpisodeAuthorOptions, selectWorkspaceEpisodeAuthorOptions } from 'app/store/Episodes/Episodes.selectors';
import { selectFilter } from 'app/store/page/page.selectors';
// Mui
import { Box, Checkbox } from '@mui/material';
// Components
import { Autocomplete } from 'app/components/Mui/Autocompletes';
import { PagesToolbarActions } from 'app/store/pagesToolbar/pagesToolbar.slice';

const EpisodeAuthorsSelect = () => {
  const { t } = useTranslation();

  const { documentId } = useParams<{ documentId:string; }>();

  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const filter:any = useAppSelector(selectFilter);
  const documentEpisodeAuthorOptions = useAppSelector((state:RootState) => selectDocumentEpisodeAuthorOptions(state, {
    documentId: +documentId
  })) as (string[] | null);
  // ToDO
  const workspaceEpisodeAuthorOptions = useAppSelector(selectWorkspaceEpisodeAuthorOptions) as any;
  // End: ToDO

  const options = useMemo(() => {
    // if no documentId, it will mean that we are on the workspace page
    if ( !documentEpisodeAuthorOptions ) return workspaceEpisodeAuthorOptions || [];
    return documentEpisodeAuthorOptions;
  }, [workspaceEpisodeAuthorOptions, documentEpisodeAuthorOptions]);

  const handleChange = (newValue:unknown) => {
    dispatch(PageActions.setFilter('authors', newValue as number[]));
    dispatch(PagesToolbarActions.clearSelectedPages());
    forceVisible();
  }

  return (
    <Autocomplete
      sx={{
        '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
          minWidth: 'unset !important',
        }
      }}
      value={filter.authors}
      onChange={handleChange}
      options={options}
      TextFieldProps={{
        label: t('labels.authors'),
        size: 'small',
        margin: 'none'
      }}
      ListboxProps={{
        sx: {
          // Reduce option horizontal padding
          '& .MuiAutocomplete-option': {
            px: 2
          }
        }
      }}
      multiple
      limitTags={2}
      getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
      renderOption={(props, option, { index }) => (
        <Box component="li" {...props} key={`${option.value}-${index}`}>
          <Checkbox sx={{ p: 1, pr: 3 }} checked={filter.authors.includes(option.value as number)} size="small" />
          {option.label}
        </Box>
      )}
      ChipProps={{
        sx: { maxWidth: `92px !important` },
        size: 'small'
      }}
      disableCloseOnSelect
    />
  )
}

export default EpisodeAuthorsSelect;
